import { useState, useCallback, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { add, format, parseISO } from 'date-fns';

import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';

import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
} from '@mui/icons-material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import WhatsAppModal from '../../components/WhatsAppModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import QrcodeModal from '../../components/QrcodeModal';
import ForbiddenPage from '../../components/ForbiddenPage';
import { Can } from '../../components/Can';
import ToolTip from '../../components/ToolTips';

import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext';
import toastError from '../../toasts/toastError';
import formatSerializedId from '../../utils/formatSerializedId';
import { AuthContext } from '../../context/Auth/AuthContext';
import usePlans from '../../hooks/usePlans';
import moment from 'moment';
import { getStyled } from './styles';

function CircularProgressWithLabel(props) {
  const theme = useTheme();

  return (
    <Box position='relative' display='inline-flex'>
      <CircularProgress variant='determinate' {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position='absolute'
        display='flex'
        alignItems='center'
        justifyContent='center'
      >
        <Typography
          variant='caption'
          component='div'
          sx={{ color: theme.custom.colors.text }}
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const IconChannel = ({ channel }) => {
  const icons = {
    facebook: <Facebook sx={{ color: '#3b5998' }} />,
    instagram: <Instagram sx={{ color: '#e1306c' }} />,
    whatsapp: <WhatsApp sx={{ color: '#25d366' }} />,
  };
  return icons[channel] || <span>error</span>;
};

const Connections = () => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppsList, setWhatsAppsList] = useState(whatsApps);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const { handleLogout } = useContext(AuthContext);
  const navigate = useNavigate();
  const confirmationModalInitialState = {
    action: '',
    title: '',
    message: '',
    whatsAppId: '',
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState,
  );
  const [planConfig, setPlanConfig] = useState(false);

  const { user, socket } = useContext(AuthContext);

  const companyId = user.companyId;

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    setWhatsAppsList(whatsApps);
  }, [whatsApps]);

  useEffect(() => {
    async function fetchData() {
      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        setPlanConfig(planConfigs);
      } catch (error) {
        console.error('Erro ao buscar plano:', error);
        toastError(error);
      }
    }
    fetchData();
  }, [companyId, getPlanCompany]);

  const isBeforeDueDate = moment().isBefore(moment(user.company.dueDate));

  if (!isBeforeDueDate) {
    handleLogout();
  }

  const handleFacebookLogin = (channelType) => {
    const clientId = import.meta.env.VITE_FACEBOOK_APP_ID;
    const redirectUri = `${window.location.origin}/auth/facebook/callback`;
    let scope =
      'public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management';

    if (channelType === 'instagram') {
      scope =
        'public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management';
    }

    window.location.href = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;
  };

  useEffect(() => {
    if (!socket) return;

    const handleImportMessages = (data) => {
      if (data.action === 'refresh') {
        setStatusImport([]);
        navigate(0);
      }
      if (data.action === 'update') {
        setStatusImport(data.status);
      }
    };

    socket.on(`importMessages-${user.companyId}`, handleImportMessages);

    return () => {
      socket.off(`importMessages-${user.companyId}`, handleImportMessages);
    };
  }, [socket, user.companyId, navigate]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    setMenuAnchorEl(null);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === 'disconnect') {
      setConfirmModalInfo({
        action: action,
        title: i18n.t('connections.confirmationModal.disconnectTitle'),
        message: i18n.t('connections.confirmationModal.disconnectMessage'),
        whatsAppId: whatsAppId,
      });
    }

    if (action === 'delete') {
      setConfirmModalInfo({
        action: action,
        title: i18n.t('connections.confirmationModal.deleteTitle'),
        message: i18n.t('connections.confirmationModal.deleteMessage'),
        whatsAppId: whatsAppId,
      });
    }
    if (action === 'closedImported') {
      setConfirmModalInfo({
        action: action,
        title: i18n.t('connections.confirmationModal.closedImportedTitle'),
        message: i18n.t('connections.confirmationModal.closedImportedMessage'),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === 'disconnect') {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === 'delete') {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toastSuccess(i18n.t('connections.toasts.deleted'));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === 'closedImported') {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toastSuccess(i18n.t('connections.toasts.closedimported'));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
    setConfirmModalOpen(false);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === 'renderButtonCloseTickets') {
      return (
        <Button
          sx={{ marginLeft: 12 }}
          size='small'
          variant='outlined'
          color='primary'
          onClick={() => {
            handleOpenConfirmationModal('closedImported', whatsApp.id);
          }}
        >
          {i18n.t('connections.buttons.closedImported')}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      let isTimeStamp = !isNaN(
        new Date(Math.floor(whatsApp?.statusImportMessages)).getTime(),
      );

      if (isTimeStamp) {
        const ultimoStatus = new Date(
          Math.floor(whatsApp?.statusImportMessages),
        ).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <>
              <Button
                disabled
                sx={{ marginLeft: 12 }}
                size='small'
                endIcon={
                  <CircularProgress size={12} sx={classes.buttonProgress} />
                }
                variant='outlined'
                color='primary'
              >
                {i18n.t('connections.buttons.preparing')}
              </Button>
            </>
          );
        }
      }
    }
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <>
        {whatsApp.status === 'qrcode' && (
          <Can
            role={
              user.profile === 'user' && user.allowConnections === 'enabled'
                ? 'admin'
                : user.profile
            }
            perform='connections-page:addConnection'
            yes={() => (
              <Button
                size='small'
                variant='outlined'
                sx={theme.custom.editButton}
                onClick={() => handleOpenQrModal(whatsApp)}
              >
                {i18n.t('connections.buttons.qrcode')}
              </Button>
            )}
          />
        )}
        {whatsApp.status === 'DISCONNECTED' && (
          <Can
            role={
              user.profile === 'user' && user.allowConnections === 'enabled'
                ? 'admin'
                : user.profile
            }
            perform='connections-page:addConnection'
            yes={() => (
              <>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                  sx={theme.custom.editButton}
                >
                  {i18n.t('connections.buttons.tryAgain')}
                </Button>{' '}
                <Button
                  size='small'
                  variant='outlined'
                  sx={theme.custom.cancelButton}
                  onClick={() => handleRequestNewQrCode(whatsApp.id)}
                >
                  {i18n.t('connections.buttons.newQr')}
                </Button>
              </>
            )}
          />
        )}
        {(whatsApp.status === 'CONNECTED' ||
          whatsApp.status === 'PAIRING' ||
          whatsApp.status === 'TIMEOUT') && (
          <Can
            role={user.profile}
            perform='connections-page:addConnection'
            yes={() => (
              <>
                <Button
                  size='small'
                  variant='outlined'
                  sx={theme.custom.editButton}
                  onClick={() => {
                    handleOpenConfirmationModal('disconnect', whatsApp.id);
                  }}
                >
                  {i18n.t('connections.buttons.disconnect')}
                </Button>

                {renderImportButton(whatsApp)}
              </>
            )}
          />
        )}
        {whatsApp.status === 'OPENING' && (
          <Button size='small' variant='outlined' disabled color='default'>
            {i18n.t('connections.buttons.connecting')}
          </Button>
        )}
      </>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    return (
      <div style={classes.customTableCell}>
        {whatsApp.status === 'DISCONNECTED' && (
          <ToolTip
            title={i18n.t('connections.toolTips.disconnected.title')}
            content={i18n.t('connections.toolTips.disconnected.content')}
          >
            <SignalCellularConnectedNoInternet0Bar color='secondary' />
          </ToolTip>
        )}
        {whatsApp.status === 'OPENING' && (
          <CircularProgress size={24} sx={classes.buttonProgress} />
        )}
        {whatsApp.status === 'qrcode' && (
          <ToolTip
            title={i18n.t('connections.toolTips.qrcode.title')}
            content={i18n.t('connections.toolTips.qrcode.content')}
          >
            <CropFree />
          </ToolTip>
        )}
        {whatsApp.status === 'CONNECTED' && (
          <ToolTip title={i18n.t('connections.toolTips.connected.title')}>
            <SignalCellular4Bar
              sx={{ color: theme.custom.colors.confirmButton }}
            />
          </ToolTip>
        )}
        {(whatsApp.status === 'TIMEOUT' || whatsApp.status === 'PAIRING') && (
          <ToolTip
            title={i18n.t('connections.toolTips.timeout.title')}
            content={i18n.t('connections.toolTips.timeout.content')}
          >
            <SignalCellularConnectedNoInternet2Bar color='secondary' />
          </ToolTip>
        )}
      </div>
    );
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toastSuccess(i18n.t('connections.waitConnection'));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />
      {user.profile === 'user' && user.allowConnections === 'disabled' ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>
              {i18n.t('connections.title')} ({whatsAppsList.length})
            </Title>
            <MainHeaderButtonsWrapper>
              <Button
                variant='outlined'
                sx={{
                  ...theme.custom.editButton,
                  marginLeft: theme.spacing(1),
                }}
                onClick={restartWhatsapps}
              >
                {i18n.t('connections.restartConnections')}
              </Button>

              <Button
                variant='outlined'
                sx={{
                  ...theme.custom.editButton,
                  marginLeft: theme.spacing(1),
                }}
                onClick={() =>
                  openInNewTab(
                    `https://wa.me/${import.meta.env.VITE_NUMBER_SUPPORT}`,
                  )
                }
              >
                {i18n.t('connections.callSupport')}
              </Button>

              <Can
                role={user.profile}
                perform='connections-page:addConnection'
                yes={() => (
                  <>
                    <Button
                      variant='outlined'
                      sx={{
                        ...theme.custom.editButton,
                        marginLeft: theme.spacing(1),
                      }}
                      onClick={handleMenuOpen}
                    >
                      {i18n.t('connections.newConnection')}
                    </Button>
                    <Menu
                      anchorEl={menuAnchorEl}
                      open={Boolean(menuAnchorEl)}
                      onClose={handleMenuClose}
                      slotProps={theme.custom.menuProps}
                    >
                      <MenuItem
                        disabled={planConfig?.plan?.useWhatsapp ? false : true}
                        onClick={() => {
                          handleOpenWhatsAppModal();
                          handleMenuClose();
                        }}
                      >
                        <WhatsApp
                          fontSize='small'
                          sx={{
                            marginRight: '10px',
                            color: '#25D366',
                          }}
                        />
                        WhatsApp
                      </MenuItem>
                      <MenuItem
                        disabled={planConfig?.plan?.useFacebook ? false : true}
                        onClick={() => {
                          handleFacebookLogin('facebook');
                          handleMenuClose();
                        }}
                      >
                        <Facebook
                          fontSize='small'
                          sx={{
                            marginRight: '10px',
                            color: '#3b5998',
                          }}
                        />
                        Facebook
                      </MenuItem>
                      <MenuItem
                        disabled={planConfig?.plan?.useInstagram ? false : true}
                        onClick={() => {
                          handleFacebookLogin('instagram');
                          handleMenuClose();
                        }}
                      >
                        <Instagram
                          fontSize='small'
                          sx={{
                            marginRight: '10px',
                            color: '#e1306c',
                          }}
                        />
                        Instagram
                      </MenuItem>
                    </Menu>
                  </>
                )}
              />
            </MainHeaderButtonsWrapper>
          </MainHeader>

          {statusImport?.all ? (
            <>
              <div style={{ margin: 'auto', marginBottom: 12 }}>
                <Card sx={classes.root}>
                  <CardContent className={classes.content}>
                    <Typography component='h5' variant='h5'>
                      {statusImport?.this === -1
                        ? i18n.t('connections.buttons.preparing')
                        : i18n.t('connections.buttons.importing')}
                    </Typography>
                    {statusImport?.this === -1 ? (
                      <Typography component='h6' variant='h6' align='center'>
                        <CircularProgress size={24} />
                      </Typography>
                    ) : (
                      <>
                        <Typography component='h6' variant='h6' align='center'>
                          {`${i18n.t(`connections.typography.processed`)} ${
                            statusImport?.this
                          } ${i18n.t(`connections.typography.in`)} ${
                            statusImport?.all
                          }  ${i18n.t(`connections.typography.date`)}: ${
                            statusImport?.date
                          } `}
                        </Typography>
                        <Typography align='center'>
                          <CircularProgressWithLabel
                            sx={{ margin: 'auto' }}
                            value={
                              (statusImport?.this / statusImport?.all) * 100
                            }
                          />
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}

          <Paper sx={classes.mainPaper} variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>Channel</TableCell>
                  <TableCell align='center'>
                    {i18n.t('connections.table.name')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('connections.table.number')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('connections.table.status')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('connections.table.session')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('connections.table.lastUpdate')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('connections.table.default')}
                  </TableCell>
                  <Can
                    role={
                      user.profile === 'user' &&
                      user.allowConnections === 'enabled'
                        ? 'admin'
                        : user.profile
                    }
                    perform='connections-page:addConnection'
                    yes={() => (
                      <TableCell align='center'>
                        {i18n.t('connections.table.actions')}
                      </TableCell>
                    )}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRowSkeleton />
                ) : (
                  <>
                    {whatsAppsList?.length > 0 &&
                      whatsAppsList.map((whatsApp) => (
                        <TableRow key={whatsApp.id}>
                          <TableCell align='center'>
                            <IconChannel channel={whatsApp.channel} />
                          </TableCell>
                          <TableCell align='center'>{whatsApp.name}</TableCell>
                          <TableCell align='center'>
                            {whatsApp.number &&
                            whatsApp.channel === 'whatsapp' ? (
                              <>{formatSerializedId(whatsApp.number)}</>
                            ) : (
                              whatsApp.number
                            )}
                          </TableCell>
                          <TableCell align='center'>
                            {renderStatusToolTips(whatsApp)}
                          </TableCell>
                          <TableCell align='center'>
                            {renderActionButtons(whatsApp)}
                          </TableCell>
                          <TableCell align='center'>
                            {format(
                              parseISO(whatsApp.updatedAt),
                              'dd/MM/yy HH:mm',
                            )}
                          </TableCell>
                          <TableCell align='center'>
                            {whatsApp.isDefault && (
                              <div style={classes.customTableCell}>
                                <CheckCircle
                                  sx={{
                                    color: theme.custom.colors.confirmButton,
                                  }}
                                />
                              </div>
                            )}
                          </TableCell>
                          <Can
                            role={user.profile}
                            perform='connections-page:addConnection'
                            yes={() => (
                              <TableCell align='center'>
                                <IconButton
                                  size='small'
                                  onClick={() => handleEditWhatsApp(whatsApp)}
                                >
                                  <Edit />
                                </IconButton>

                                <IconButton
                                  size='small'
                                  onClick={() => {
                                    handleOpenConfirmationModal(
                                      'delete',
                                      whatsApp.id,
                                    );
                                  }}
                                >
                                  <DeleteOutline
                                    sx={{
                                      color: theme.custom.colors.cancelButton,
                                    }}
                                  />
                                </IconButton>
                              </TableCell>
                            )}
                          />
                        </TableRow>
                      ))}
                  </>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Connections;
